import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import LinkHubListView from "../../components/product-manager/LinkHubListView";
import EditLinkDialog from "../../components/product-manager/EditLinkDialog";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import { LINK_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useLinks } from "../../hooks/useLinks";
import { apiService } from "../../api/services/apiService";
import type { Link } from "../../types";
import { EntityStatus } from "../../types";
import { resolveLinkDescription, resolveLinkUrl } from "../../utils/linkFields";
import { pmHubAddPath } from "../../utils/pmHubRoutes";

type ViewMode = "card" | "list";

const LinkHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState();
  const { links: fetched, loading, error, refresh } = useLinks();
  const [links, setLinks] = useState<Link[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [singleDeleteLink, setSingleDeleteLink] = useState<Link | null>(null);

  useEffect(() => {
    setLinks(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () => filterByHubState(links, hubState),
    [links, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const pageIds = useMemo(
    () => paginated.map((l) => l.uuid),
    [paginated],
  );

  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    setSelectedIds(new Set());
  }, [hubState.page, hubState.debouncedQ, hubState.statuses]);

  const editLink = useMemo(
    () => links.find((l) => l.uuid === hubState.editUuid) ?? null,
    [links, hubState.editUuid],
  );

  const toggleSelect = useCallback((uuid: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allPageSelected, pageIds]);

  const clearSelection = () => setSelectedIds(new Set());

  const handleToggle = async (link: Link) => {
    setToggleLoadingId(link.uuid);
    const newStatus =
      link.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateLink(link.uuid, {
        name: link.name,
        entityClass: link.entityClass ?? undefined,
        description: link.description ?? undefined,
        status: newStatus,
      });
      setLinks((prev) =>
        prev.map((l) => (l.uuid === updated.uuid ? updated : l)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  const removeLinksFromState = (uuids: string[]) => {
    const removed = new Set(uuids);
    setLinks((prev) => prev.filter((l) => !removed.has(l.uuid)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      uuids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleDeleteOne = async (link: Link) => {
    try {
      await apiService.deleteLink(link.uuid);
      removeLinksFromState([link.uuid]);
      setSingleDeleteLink(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to delete link");
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((uuid) => apiService.deleteLink(uuid)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const succeeded = ids.filter((_, i) => results[i].status === "fulfilled");
      removeLinksFromState(succeeded);
      if (failed > 0) {
        alert(
          `Deleted ${succeeded.length} of ${ids.length} link(s). ${failed} failed.`,
        );
      }
      setBulkDeleteOpen(false);
      if (succeeded.length > 0) await refresh();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to delete links");
    } finally {
      setBulkDeleting(false);
    }
  };

  const selectedCount = selectedIds.size;

  return (
    <>
      <EntityHubLayout
        hubTitle="Link Hub"
        hubDescription="Manage links with individual and bulk create, update, and enable or disable."
        addPath={pmHubAddPath("link")}
        addLabel="Add Link"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            {selectedCount > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {selectedCount} selected
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setBulkDeleteOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  Delete selected
                </Button>
                <Button
                  size="small"
                  onClick={clearSelection}
                  sx={{ textTransform: "none" }}
                >
                  Clear selection
                </Button>
              </>
            )}
          </Box>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, value: ViewMode | null) => {
              if (value) setViewMode(value);
            }}
            aria-label="View mode"
          >
            <ToggleButton value="card" aria-label="Card view">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === "list" ? (
          <LinkHubListView
            links={paginated}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            allPageSelected={allPageSelected}
            somePageSelected={somePageSelected}
            onEdit={(link) => hubState.setEditUuid(link.uuid)}
            onDelete={(link) => setSingleDeleteLink(link)}
            onToggleEnabled={handleToggle}
            toggleLoadingId={toggleLoadingId}
          />
        ) : (
          <Grid container spacing={2}>
            {paginated.map((link) => {
              const selected = selectedIds.has(link.uuid);
              return (
                <Grid item xs={12} sm={6} md={4} key={link.uuid}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      height: "100%",
                    }}
                  >
                    <Checkbox
                      checked={selected}
                      onChange={() => toggleSelect(link.uuid)}
                      sx={{ mt: 1.25, flexShrink: 0 }}
                      inputProps={{
                        "aria-label": `Select ${link.name}`,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <EntityAssetCard
                        title={link.name}
                        subtitle={
                          resolveLinkUrl(link) ||
                          resolveLinkDescription(link) ||
                          undefined
                        }
                        status={link.status}
                        enabled={link.status === EntityStatus.ACTIVE}
                        toggleLoading={toggleLoadingId === link.uuid}
                        onToggleEnabled={() => handleToggle(link)}
                        onEdit={() => hubState.setEditUuid(link.uuid)}
                        onDelete={async () => {
                          await apiService.deleteLink(link.uuid);
                          removeLinksFromState([link.uuid]);
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              );
            })}
            {!loading && filtered.length === 0 && (
              <Grid item xs={12}>
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No links match your filters.
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        config={LINK_BULK_CONFIG}
        onClose={() => setBulkOpen(false)}
        onComplete={() => refresh()}
      />

      <EditLinkDialog
        link={editLink}
        open={Boolean(hubState.editUuid && editLink)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) =>
          setLinks((prev) =>
            prev.map((l) => (l.uuid === updated.uuid ? updated : l)),
          )
        }
      />

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => !bulkDeleting && setBulkDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete selected links?</DialogTitle>
        <DialogContent>
          Delete {selectedCount} link{selectedCount === 1 ? "" : "s"}? This
          cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteOpen(false)}
            disabled={bulkDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
          >
            {bulkDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(singleDeleteLink)}
        onClose={() => setSingleDeleteLink(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete link?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete &quot;{singleDeleteLink?.name}&quot;?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteLink(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              singleDeleteLink && handleDeleteOne(singleDeleteLink)
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkHubPage;
